import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, UserCheck, Star, CheckCircle2, Clock, Award, Languages, MapPin, Loader2 } from "lucide-react";

const INSPECTION_TYPES = [
  { key: "initial", label: "فحص أولي (قبل التصنيع)" },
  { key: "incoming_material", label: "فحص المواد الواردة" },
  { key: "in_process", label: "فحص أثناء التصنيع" },
  { key: "final", label: "فحص نهائي (قبل التسليم)" },
  { key: "customer_witness", label: "فحص بحضور العميل" },
];

const INSPECTION_LOCATIONS = [
  { key: "workshop", label: "في الورشة" },
  { key: "factory", label: "في مصنع العميل" },
  { key: "customer_site", label: "في موقع العميل" },
];

const AVAILABILITY_LABELS = {
  available: { text: "متاح", cls: "bg-emerald-100 text-emerald-800" },
  busy: { text: "مشغول", cls: "bg-amber-100 text-amber-800" },
  unavailable: { text: "غير متاح", cls: "bg-red-100 text-red-800" },
};

function InspectorCard({ inspector, selected, onSelect }) {
  const avail = AVAILABILITY_LABELS[inspector.availability] || AVAILABILITY_LABELS.available;
  const rating = inspector.rating || 0;
  const successPct = Math.round((inspector.success_rate || 0) * 100);
  return (
    <button
      type="button"
      onClick={() => onSelect(inspector.id)}
      className={`w-full text-start card-industrial p-4 transition-all ${
        selected ? "border-brand-gold ring-2 ring-brand-gold/30 bg-brand-gold-soft/30" : "hover:border-slate-300"
      }`}
      data-testid={`inspector-card-${inspector.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-brand-navy text-white flex items-center justify-center font-bold text-lg shrink-0">
          {(inspector.name || "?").trim().charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-bold text-brand-navy">{inspector.name}</div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${avail.cls}`}>{avail.text}</span>
          </div>
          {inspector.specialization && (
            <div className="text-xs text-slate-600 mt-0.5">{inspector.specialization}</div>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs">
            <span className="inline-flex items-center gap-1 text-amber-700">
              <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" /> {rating.toFixed(1)}/5
            </span>
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="w-3.5 h-3.5" /> {successPct}% نجاح
            </span>
            <span className="inline-flex items-center gap-1 text-slate-600">
              <Award className="w-3.5 h-3.5" /> {inspector.completed_inspections || 0} فحص
            </span>
            {inspector.years_experience ? (
              <span className="inline-flex items-center gap-1 text-slate-600">
                <Clock className="w-3.5 h-3.5" /> {inspector.years_experience} سنة خبرة
              </span>
            ) : null}
          </div>
          {inspector.cities_covered?.length > 0 && (
            <div className="flex items-start gap-1 mt-1.5 text-xs text-slate-500">
              <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
              <span className="truncate">{inspector.cities_covered.join("، ")}</span>
            </div>
          )}
          {inspector.industries_covered?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {inspector.industries_covered.slice(0, 4).map((ind) => (
                <span key={ind} className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                  {ind}
                </span>
              ))}
              {inspector.industries_covered.length > 4 && (
                <span className="text-[10px] text-slate-500">+{inspector.industries_covered.length - 4}</span>
              )}
            </div>
          )}
          {inspector.languages?.length > 0 && (
            <div className="flex items-center gap-1 mt-1.5 text-xs text-slate-500">
              <Languages className="w-3 h-3" />
              <span>{inspector.languages.join(" • ")}</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

export default function AssignInspectorDialog({ open, onOpenChange, requestDoc, onAssigned }) {
  const [inspectors, setInspectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({ q: "", city: "", specialization: "", min_rating: "", availability: "" });
  const [selectedId, setSelectedId] = useState(requestDoc?.assigned_inspector || "");
  const [scheduling, setScheduling] = useState({
    inspection_type: "initial",
    inspection_location: "workshop",
    inspection_date: "",
    duration_minutes: 120,
    timezone: "Asia/Riyadh",
    notes: "",
  });

  const loadInspectors = async (f) => {
    setLoading(true);
    try {
      const params = {};
      if (f.q) params.q = f.q;
      if (f.city) params.city = f.city;
      if (f.specialization) params.specialization = f.specialization;
      if (f.min_rating) params.min_rating = Number(f.min_rating);
      if (f.availability) params.availability = f.availability;
      const { data } = await api.get("/inspectors", { params });
      setInspectors(data.inspectors || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  // Reset dialog state whenever it opens for a new request.
  // Depend only on primitive keys from requestDoc so a fresh parent-object
  // reference doesn't retrigger this effect and cause a render loop.
  const requestId = requestDoc?.id;
  const defaultCity = requestDoc?.inspection_city || "";
  const defaultInspector = requestDoc?.assigned_inspector || "";
  const defaultDate = requestDoc?.inspection_date || "";
  useEffect(() => {
    if (!open) return;
    const initial = { q: "", city: defaultCity, specialization: "", min_rating: "", availability: "" };
    setFilters(initial);
    setSelectedId(defaultInspector);
    setScheduling((prev) => ({
      ...prev,
      inspection_date: defaultDate ? String(defaultDate).slice(0, 16) : "",
    }));
    loadInspectors(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requestId, defaultCity, defaultInspector, defaultDate]);

  const applyFilters = () => loadInspectors(filters);
  const resetFilters = () => {
    const empty = { q: "", city: "", specialization: "", min_rating: "", availability: "" };
    setFilters(empty);
    loadInspectors(empty);
  };

  const selected = useMemo(
    () => inspectors.find((i) => i.id === selectedId) || null,
    [inspectors, selectedId]
  );

  const submit = async () => {
    if (!selectedId) {
      toast.error("اختر مفتشاً أولاً");
      return;
    }
    if (!scheduling.inspection_date) {
      toast.error("حدد موعد الفحص");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        inspector_id: selectedId,
        inspection_type: scheduling.inspection_type,
        inspection_location: scheduling.inspection_location,
        inspection_date: new Date(scheduling.inspection_date).toISOString(),
        duration_minutes: Number(scheduling.duration_minutes) || 120,
        timezone: scheduling.timezone || "Asia/Riyadh",
        notes: scheduling.notes || null,
      };
      await api.post(`/requests/${requestDoc.id}/assign-inspector`, payload);
      toast.success("تم إسناد المفتش وجدولة الزيارة");
      onOpenChange(false);
      onAssigned?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSubmitting(false);
  };

  const upd = (k, v) => setScheduling((prev) => ({ ...prev, [k]: v }));
  const updFilter = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="assign-inspector-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-brand-gold" /> إسناد مفتش وجدولة الفحص
          </DialogTitle>
        </DialogHeader>

        {/* Reassignment warning */}
        {requestDoc?.assigned_inspector && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="reassignment-warning">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
            <div className="flex-1">
              <div className="font-semibold">إعادة إسناد مفتش</div>
              <div className="text-xs text-amber-800 mt-0.5">
                هذا الطلب لديه مفتش مُسند بالفعل
                {requestDoc?.assigned_inspector_name ? ` (${requestDoc.assigned_inspector_name})` : ""}
                {requestDoc?.inspection_scheduled_at ? ` بموعد ${String(requestDoc.inspection_scheduled_at).slice(0, 16).replace("T", " ")}` : ""}.
                تأكيدك هنا سيُلغي الجدولة السابقة ويُخطر جميع الأطراف بالمفتش والموعد الجديد.
              </div>
            </div>
          </div>
        )}

        {/* Context strip */}
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="assign-context">
          <div><span className="text-slate-500">الطلب:</span> <span className="font-semibold">{requestDoc?.request_number}</span></div>
          <div><span className="text-slate-500">القطعة:</span> {requestDoc?.part_name || "—"}</div>
          <div><span className="text-slate-500">المدينة:</span> {requestDoc?.inspection_city || "غير محدد"}</div>
          <div><span className="text-slate-500">التاريخ المفضل:</span> {requestDoc?.inspection_date?.slice(0, 10) || "—"}</div>
        </div>

        <div className="grid md:grid-cols-5 gap-4 mt-4">
          {/* Left: filters + inspector list */}
          <div className="md:col-span-3 space-y-3">
            <div className="bg-white border border-slate-200 rounded-md p-3" data-testid="inspector-filters">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">بحث بالاسم/التخصص</Label>
                  <Input value={filters.q} onChange={(e) => updFilter("q", e.target.value)} className="mt-1 h-9" data-testid="filter-inspector-q" />
                </div>
                <div>
                  <Label className="text-xs">المدينة</Label>
                  <Input value={filters.city} onChange={(e) => updFilter("city", e.target.value)} className="mt-1 h-9" data-testid="filter-inspector-city" />
                </div>
                <div>
                  <Label className="text-xs">التخصص</Label>
                  <Input placeholder="مثال: ميكانيكا" value={filters.specialization} onChange={(e) => updFilter("specialization", e.target.value)} className="mt-1 h-9" data-testid="filter-inspector-specialization" />
                </div>
                <div>
                  <Label className="text-xs">أدنى تقييم</Label>
                  <Select value={filters.min_rating} onValueChange={(v) => updFilter("min_rating", v === "any" ? "" : v)}>
                    <SelectTrigger className="mt-1 h-9" data-testid="filter-inspector-rating"><SelectValue placeholder="أي تقييم" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">أي تقييم</SelectItem>
                      <SelectItem value="4">4+ نجوم</SelectItem>
                      <SelectItem value="4.5">4.5+ نجوم</SelectItem>
                      <SelectItem value="4.8">4.8+ نجوم</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">التوفر</Label>
                  <Select value={filters.availability} onValueChange={(v) => updFilter("availability", v === "any" ? "" : v)}>
                    <SelectTrigger className="mt-1 h-9" data-testid="filter-inspector-availability"><SelectValue placeholder="الكل" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">الكل</SelectItem>
                      <SelectItem value="available">متاح</SelectItem>
                      <SelectItem value="busy">مشغول</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button size="sm" variant="outline" onClick={resetFilters} className="flex-1" data-testid="reset-inspector-filters-btn">إعادة ضبط</Button>
                  <Button size="sm" className="btn-cta flex-1" onClick={applyFilters} data-testid="apply-inspector-filters-btn">تطبيق</Button>
                </div>
              </div>
            </div>

            <div className="text-xs text-slate-600 flex items-center justify-between">
              <span data-testid="inspectors-count">النتائج: {inspectors.length} مفتش</span>
              {selected && <span className="font-semibold text-brand-gold">مختار: {selected.name}</span>}
            </div>

            <div className="max-h-[420px] overflow-y-auto space-y-2 pe-1" data-testid="inspectors-list">
              {loading && <div className="text-center py-8 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
              {!loading && inspectors.map((ins) => (
                <InspectorCard key={ins.id} inspector={ins} selected={selectedId === ins.id} onSelect={setSelectedId} />
              ))}
              {!loading && inspectors.length === 0 && (
                <div className="text-center py-8 text-slate-500 text-sm">لا يوجد مفتشون مطابقون للفلاتر الحالية.</div>
              )}
            </div>
          </div>

          {/* Right: scheduling panel */}
          <div className="md:col-span-2 space-y-3" data-testid="scheduling-panel">
            <div className="bg-brand-gold-soft border border-brand-gold/40 rounded-md p-3 text-xs text-slate-700">
              يتم إخطار المفتش والعميل والورشة (إذا وُجدت طلبية مرتبطة) تلقائياً بعد الإسناد،
              وتحديث حالة الطلب إلى «تم جدولة الفحص».
            </div>

            <div>
              <Label>نوع الفحص</Label>
              <Select value={scheduling.inspection_type} onValueChange={(v) => upd("inspection_type", v)}>
                <SelectTrigger className="mt-1.5" data-testid="inspection-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INSPECTION_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>موقع الفحص</Label>
              <Select value={scheduling.inspection_location} onValueChange={(v) => upd("inspection_location", v)}>
                <SelectTrigger className="mt-1.5" data-testid="inspection-location-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INSPECTION_LOCATIONS.map((l) => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>موعد الفحص</Label>
              <Input type="datetime-local" value={scheduling.inspection_date} onChange={(e) => upd("inspection_date", e.target.value)} className="mt-1.5" data-testid="inspection-datetime-input" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>المدة (دقيقة)</Label>
                <Input type="number" min="15" step="15" value={scheduling.duration_minutes} onChange={(e) => upd("duration_minutes", e.target.value)} className="mt-1.5" data-testid="inspection-duration-input" />
              </div>
              <div>
                <Label>المنطقة الزمنية</Label>
                <Select value={scheduling.timezone} onValueChange={(v) => upd("timezone", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="inspection-timezone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Asia/Riyadh">توقيت الرياض</SelectItem>
                    <SelectItem value="Asia/Dubai">توقيت دبي</SelectItem>
                    <SelectItem value="UTC">UTC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>ملاحظات للمفتش</Label>
              <Textarea rows={3} value={scheduling.notes} onChange={(e) => upd("notes", e.target.value)} placeholder="تعليمات الوصول، متطلبات السلامة، أدوات القياس المطلوبة..." className="mt-1.5" data-testid="inspection-notes-input" />
            </div>

            {selected && (
              <div className="border border-slate-200 rounded-md p-3 bg-white text-xs" data-testid="selected-inspector-summary">
                <div className="font-semibold text-brand-navy mb-1">ملخص الاختيار</div>
                <div>{selected.name} — {selected.specialization || "عام"}</div>
                <div className="text-slate-500 mt-1">
                  {selected.rating?.toFixed(1)}/5 • {Math.round((selected.success_rate || 0) * 100)}% نجاح • {selected.completed_inspections || 0} فحص
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-assign-inspector-btn">إلغاء</Button>
          <Button className="btn-cta" onClick={submit} disabled={submitting || !selectedId || !scheduling.inspection_date} data-testid="confirm-assign-inspector-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><UserCheck className="w-4 h-4 me-1" /> إسناد وجدولة</>)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
